<template>
  <div class="flex items-center gap-2" role="group" :aria-label="$t('coach.feedback_thumbs.question')">
    <span class="text-text-secondary text-xs">{{ $t('coach.feedback_thumbs.question') }}</span>
    <button
      v-for="option in OPTIONS"
      :key="option.value"
      type="button"
      class="w-8 h-8 rounded-md flex items-center justify-center duration-300 disabled:cursor-not-allowed"
      :class="current === option.value ? option.on : 'text-text-secondary hover:text-text-primary hover:bg-gray-800'"
      :aria-pressed="current === option.value"
      :aria-label="$t(option.label)"
      :disabled="saving"
      @click="rate(option.value)"
    >
      <component :is="current === option.value ? option.solid : option.outline" class="w-4 h-4" />
    </button>
    <span class="text-danger-400 text-xs" aria-live="polite">
      <template v-if="failed">{{ $t('coach.feedback_thumbs.failed') }}</template>
    </span>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import axios from 'axios';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/vue/24/outline';
import {
  HandThumbUpIcon as HandThumbUpSolid, HandThumbDownIcon as HandThumbDownSolid,
} from '@heroicons/vue/24/solid';

const props = defineProps({
  // The viewer's stored rating: 1, -1 or null
  rating: { type: Number, default: null },
  // POST target and body for this card's rating endpoint
  url: { type: String, required: true },
  params: { type: Object, default: () => ({}) },
});

const OPTIONS = [
  {
    value: 1,
    label: 'coach.feedback_thumbs.helpful',
    outline: HandThumbUpIcon,
    solid: HandThumbUpSolid,
    on: 'text-success-400 bg-success-900',
  },
  {
    value: -1,
    label: 'coach.feedback_thumbs.not_helpful',
    outline: HandThumbDownIcon,
    solid: HandThumbDownSolid,
    on: 'text-danger-400 bg-danger-900',
  },
];

const current = ref(props.rating);
const saving = ref(false);
const failed = ref(false);
watch(() => props.rating, (r) => { current.value = r; });

// Optimistic: the thumb lights up at once and goes back if the save fails.
// Tapping the lit thumb again clears the rating (0).
async function rate(value) {
  const previous = current.value;
  const next = previous === value ? null : value;
  current.value = next;
  saving.value = true;
  failed.value = false;
  try {
    const { data } = await axios.post(props.url, { ...props.params, rating: next ?? 0 });
    current.value = data?.rating ?? null;
  } catch {
    current.value = previous;
    failed.value = true;
  } finally {
    saving.value = false;
  }
}
</script>
